/**
 * IdentityGroupsTable — the "Groups & Distribution Lists" tab inside
 * /employees.
 *
 * Why a separate component (and a separate /api/identity-groups endpoint):
 *   • Pre-Feb-2026 we merged GW groups into the same People table, which
 *     auditors complained about — mailing lists kept getting confused for
 *     humans during access reviews.
 *   • Splitting the surface keeps the main table focused on real people
 *     while groups remain fully reviewable + governable (classify, ignore,
 *     archive — same overlay endpoints) under their own tab.
 *
 * Backend contract: GET /api/identity-groups → array of synthetic rows
 * shaped like /api/employees rows (`source: "google_workspace_group"`,
 * `identity_type` already decorated, plus `group_member_count`,
 * `group_external_member_count`, `group_members`, `group_type`).
 *
 * Out of scope for v1: search box, server-side pagination, bulk-classify.
 * The collection is tiny in practice (DLs are O(tens), not thousands), so
 * we render the whole list and rely on the visible counts column for
 * orientation.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Users2, Mail, Loader2, ExternalLink } from 'lucide-react';
import IdentityActionsMenu from './IdentityActionsMenu';

const TYPE_LABEL = {
  distribution_list: 'Distribution List',
  shared_account: 'Shared Account',
  service_account: 'Service Account',
  vendor: 'Vendor',
  non_employee: 'Non-Employee',
  contractor: 'Contractor',
  employee: 'Employee',
};

const TYPE_PILL_CLASS = {
  distribution_list: 'bg-purple-50 text-purple-700 border-purple-200',
  shared_account: 'bg-blue-50 text-blue-700 border-blue-200',
  service_account: 'bg-slate-50 text-slate-700 border-slate-200',
  vendor: 'bg-amber-50 text-amber-700 border-amber-200',
};

const IdentityGroupsTable = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [showIgnored, setShowIgnored] = useState(false);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.append('archived', showArchived ? 'all' : 'false');
      qs.append('ignored', showIgnored ? 'all' : 'false');
      const r = await axios.get(`/api/identity-groups?${qs.toString()}`);
      setRows(r.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [showArchived, showIgnored]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-8" data-testid="identity-groups-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading groups & distribution lists…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <Card className="border-dashed border-slate-200" data-testid="identity-groups-empty">
        <CardContent className="py-10 text-center">
          <Users2 className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-700">No groups or distribution lists synced yet.</p>
          <p className="text-xs text-slate-500 mt-1">
            Connect Google Workspace and run a sync — mailing lists, shared mailboxes,
            and team aliases will appear here for governance review.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="identity-groups-tab">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatChip label="Total" value={rows.length} />
        <StatChip
          label="Distribution Lists"
          value={rows.filter((r) => r.identity_type === 'distribution_list').length}
        />
        <StatChip
          label="Shared accounts"
          value={rows.filter((r) => r.identity_type === 'shared_account').length}
        />
        <StatChip
          label="Members in scope"
          value={rows.reduce((s, r) => s + (r.group_member_count || 0), 0)}
        />
      </div>

      <div className="flex items-center gap-3 text-xs text-slate-600">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="identity-groups-show-archived"
          />
          Show archived
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={showIgnored}
            onChange={(e) => setShowIgnored(e.target.checked)}
            data-testid="identity-groups-show-ignored"
          />
          Show ignored
        </label>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="identity-groups-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">Members</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600">External</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className={r.is_archived || r.is_ignored ? 'bg-slate-50/60' : ''}
                  data-testid={`identity-group-row-${r.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Users2 className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-medium text-slate-900">{r.full_name || '—'}</span>
                    </div>
                    {r.group_type && (
                      <div className="text-[11px] text-slate-500 mt-0.5 pl-6">{r.group_type}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-sm text-slate-700 font-mono">
                      <Mail className="w-3.5 h-3.5 text-slate-400" />
                      {r.email}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant="outline"
                      className={TYPE_PILL_CLASS[r.identity_type] || 'bg-slate-50 text-slate-700 border-slate-200'}
                    >
                      {TYPE_LABEL[r.identity_type] || r.identity_type}
                    </Badge>
                    {r.is_archived && (
                      <Badge className="ml-1 bg-slate-100 text-slate-600 border-slate-200" variant="outline">
                        archived
                      </Badge>
                    )}
                    {r.is_ignored && (
                      <Badge className="ml-1 bg-amber-50 text-amber-700 border-amber-200" variant="outline">
                        ignored
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {(r.group_members || []).length > 0 ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-900 underline decoration-emerald-300"
                            data-testid={`identity-group-members-trigger-${r.id}`}
                          >
                            {r.group_member_count || 0}
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 max-h-72 overflow-y-auto">
                          <div className="text-xs font-semibold uppercase text-slate-500 mb-2">
                            Members ({r.group_member_count || 0})
                          </div>
                          <ul className="space-y-1">
                            {(r.group_members || []).map((m, i) => (
                              <li key={`${r.id}-m-${i}`} className="text-sm text-slate-700 flex justify-between gap-2">
                                <span className="font-mono truncate">{m.email}</span>
                                {m.role && (
                                  <span className="text-[10px] uppercase text-slate-400">{m.role}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <span className="text-sm text-slate-500">{r.group_member_count || 0}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {r.group_external_member_count > 0 ? (
                      <Badge className="bg-red-50 text-red-700 border-red-200" variant="outline">
                        {r.group_external_member_count} external
                      </Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <IdentityActionsMenu
                      employee={r}
                      onAfterMutate={fetchGroups}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const StatChip = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-xl font-semibold text-slate-900">{value}</div>
  </div>
);

export default IdentityGroupsTable;
